import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';

type UserRole = Database['public']['Enums']['user_role'];

type UserProfileWithOrganizations = Database['public']['Tables']['users']['Row'] & {
  user_organization?: Array<{
    organization: {
      id: string;
      name: string;
      hexcode: string | null;
      image_url?: string | null;
      domain?: string | null;
    };
    is_active: boolean;
    is_main: boolean;
  }>;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfileWithOrganizations | null;
  userRole: UserRole | null;
  loading: boolean;
  /** True after Supabase opened a session from a password-recovery link. */
  isPasswordRecovery: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  changePassword: (newPassword: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: Database['public']['Tables']['users']['Update']) => Promise<{ error: any }>;
  /** Marks the forced/recovery password change as done and reloads the profile. */
  completePasswordChange: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileWithOrganizations | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          user_organization!left(
            organization:organizations(id, name, hexcode, image_url, domain),
            is_active,
            is_main
          )
        `)
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        // Create profile if it doesn't exist
        if (error.code === 'PGRST116') {
          const { data: authUser } = await supabase.auth.getUser();
          if (authUser?.user && authUser.user.email) {
            const { data: newProfile, error: insertError } = await supabase
              .from('users')
              .insert({
                id: userId,
                email: authUser.user.email,
                full_name: authUser.user.user_metadata?.full_name || null,
                role: 'normal' as const
              })
              .select()
              .single();
            
            if (newProfile) {
              setUserProfile(newProfile);
              
              // Assign document requirements for normal users
              if (newProfile.role === 'normal') {
                const { data: requirementsResult, error: reqError } = await supabase
                  .rpc('rpc_assign_requirements_to_user', { 
                    user_id: userId 
                  });
                
                if (reqError) {
                  console.error('Error assigning document requirements:', reqError);
                } else {
                  console.log('Document requirements assigned:', requirementsResult);
                }
              }
            } else if (insertError) {
              console.error('Error creating user profile:', insertError);
            }
          }
        }
      } else if (data) {
        setUserProfile(data);
      }
    } catch (err) {
      console.error('Error in fetchUserProfile:', err);
    }
  };

  useEffect(() => {
    // Check active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id).then(() => {
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setIsPasswordRecovery(true);
      if (event === 'SIGNED_OUT') setIsPasswordRecovery(false);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (!error && data.session) {
        // Immediately fetch user profile after successful login
        if (data.session.user) {
          await fetchUserProfile(data.session.user.id);
          
          // Update last_login timestamp ONLY if it's not the first login
          // First login users (last_login = null) need to change password first
          const { data: userData } = await supabase
            .from('users')
            .select('last_login')
            .eq('id', data.session.user.id)
            .single();
          
          if (userData && userData.last_login !== null) {
            await supabase
              .from('users')
              .update({ last_login: new Date().toISOString() })
              .eq('id', data.session.user.id);
          }
        }
        return { error: null };
      }
      
      return { error };
    } catch (err) {
      return { error: err };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setUserProfile(null);
      setIsPasswordRecovery(false);
      
      return { error };
    } catch (err) {
      // Clear local state even if signOut fails
      setUser(null);
      setSession(null);
      setUserProfile(null);
      
      return { error: err };
    }
  };

  const changePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    return { error };
  };

  const updateProfile = async (updates: Database['public']['Tables']['users']['Update']) => {
    if (!user) return { error: new Error('Not authenticated') };
    
    const { error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', user.id);
    
    if (!error) {
      await fetchUserProfile(user.id);
    }
    
    return { error };
  };

  const completePasswordChange = async () => {
    const currentUser = user ?? (await supabase.auth.getUser()).data.user;
    if (currentUser) {
      // last_login = null is what forces the first-login password change (see ProtectedRoute).
      const { error: updateError } = await supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', currentUser.id);
      if (updateError) {
        console.error('Error updating last_login:', updateError);
      }
      await fetchUserProfile(currentUser.id);
    }
    setIsPasswordRecovery(false);
  };

  const value = {
    user,
    session,
    userProfile,
    userRole: userProfile?.role || null,
    loading,
    isPasswordRecovery,
    signIn,
    signUp,
    signOut,
    changePassword,
    resetPassword,
    updateProfile,
    completePasswordChange,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};